import type {
  DefaultMenuItem,
  GetMainMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { APIComponentAttributes } from '@ardoq/api-types';
import { columnOperations, columnsVisibleByDefault } from './columnOperations';
import { getResetColumnsMenuItem } from './resetColumnsMenuItem';
import { getRecalculateMainMenuItem } from './recalculateMainMenuItem';
import { getBulkEditFieldMenuItem } from './bulkEditFieldMenuItem';
import { isFilterLockedForColumn } from './mapReportColumnDefinitionToAgGridFormat';
import { t } from '@ardoq/i18n';
import type { InventoryContext } from '../types';

/**
 * Checks if a separator is orphaned (trailing or consecutive).
 * Used to filter out separators that would appear at invalid positions
 * after other menu items have been removed.
 */
const isOrphanSeparator = (
  item: DefaultMenuItem,
  index: number,
  array: DefaultMenuItem[]
): boolean => {
  if (item === 'separator' && index === array.length - 1) return true;
  if (item !== 'separator') return false;
  if (index === 0) return false;

  return array[index - 1] === 'separator';
};

const itemsToBeRemovedForReportDatasource = [
  'columnChooser',
  'resetColumns',
  'pinSubMenu',
] as DefaultMenuItem[];

/**
 * Determines if a menu item should be removed for report datasources.
 * We do not show "pinSubMenu", "Choose Columns", and "Reset Columns"
 * options when editing report data.
 */
const shouldNotBeShownForReports = (item: DefaultMenuItem): boolean => {
  return itemsToBeRemovedForReportDatasource.includes(item);
};

/**
 * Returns the list of options for the menu that appears when you
 * right-click the column header in the table
 */
export const getColumnMainMenuItems = (
  params: GetMainMenuItemsParams<APIComponentAttributes, InventoryContext>
) => {
  const isReportDatasource =
    params.context.datasourceSelection.dataSourceType === 'report';
  const shouldHidePinSubMenu = params.context.config.mode === 'filterData';
  const isLockedFilter = params.column
    ? isFilterLockedForColumn(
        params.column.getColId(),
        params.context.config.fixedFilterColumnKeys
      )
    : false;
  // AG Grid external-lib callback: read locale from context per invocation.
  const i18nLocale = params.context.i18nLocale ?? {};

  const defaultItems = params.defaultItems
    .filter(item => {
      if (isReportDatasource && shouldNotBeShownForReports(item)) {
        return false;
      }
      if (shouldHidePinSubMenu && item === 'pinSubMenu') {
        return false;
      }
      if (isLockedFilter && item === 'columnFilter') {
        return false;
      }

      return true;
    })
    .filter((item, index, array) => !isOrphanSeparator(item, index, array))
    .map(item => {
      if (item === 'resetColumns') {
        return getResetColumnsMenuItem({
          api: params.api,
          i18nLocale,
          defaultVisibleColumnKeys:
            params.context.defaultVisibleColumnKeys ?? columnsVisibleByDefault,
          getFieldOrderByColumnKey: () =>
            columnOperations.getFieldOrderByName(params.context.scopeData),
        });
      }
      if (item === 'sortUnSort') {
        return {
          name: t(
            i18nLocale,
            'inventoryApp:ag.columnMenu.clearSort',
            'Clear sort'
          ),
          action: () => {
            const columnDefs = columnOperations.toLeafColumnDefs(
              params.api.getColumnDefs() ?? []
            );
            params.api.applyColumnState({
              state: columnDefs.map(
                (columnDef: ColDef<APIComponentAttributes>) => ({
                  ...columnDef,
                  colId: columnDef.colId!,
                  sort:
                    columnDef.colId === 'name' && !columnDef.sort
                      ? 'asc'
                      : null,
                })
              ),
              applyOrder: true,
            });
          },
        };
      }
      return item;
    });

  const bulkEditFieldMenuItem = getBulkEditFieldMenuItem(params);
  const bulkEditFieldItems: (
    | DefaultMenuItem
    | MenuItemDef<APIComponentAttributes>
  )[] = bulkEditFieldMenuItem
    ? [bulkEditFieldMenuItem, 'separator' as DefaultMenuItem]
    : [];

  const recalculateMainMenuItem = getRecalculateMainMenuItem(params);
  const recalculateItems: (
    | DefaultMenuItem
    | MenuItemDef<APIComponentAttributes>
  )[] = recalculateMainMenuItem
    ? ['separator' as DefaultMenuItem, recalculateMainMenuItem]
    : [];

  return [...bulkEditFieldItems, ...defaultItems, ...recalculateItems];
};
